import { useEffect, useMemo, useReducer, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import ApiKeyBar from '../components/ApiKeyBar';
import CostDisclosure from '../components/CostDisclosure';
import ErrorBoundary from '../components/ErrorBoundary';
import HistoryRail from '../components/HistoryRail';
import PanoramaViewer from '../components/PanoramaViewer';
import Presets from '../components/Presets';
import PromptBar from '../components/PromptBar';
import ShareButton from '../components/ShareButton';
import TimeSlider from '../components/TimeSlider';
import { ToastProvider, useToast } from '../components/Toast';
import YearChips from '../components/YearChips';
import { pt } from '../copy';
import {
  type GenQuality,
  GPT_IMAGE_2_MODEL,
  generateJourney,
  type Keyframe,
  OpenAIError,
} from '../lib/openai';
import {
  clearApiKey,
  clearHistory,
  getApiKey,
  getHistory,
  pushHistory,
  type StoredJourney,
  setApiKey,
} from '../lib/storage';

type Status = 'idle' | 'loading' | 'ready' | 'partial' | 'error';

type State = {
  apiKey: string;
  location: string;
  prompt: string;
  years: number[];
  quality: GenQuality;
  status: Status;
  frames: Keyframe[];
  failures: Array<{ year: number; error: Error }>;
  t: number;
  history: StoredJourney[];
  inlineError: string;
};

type Action =
  | { type: 'apiKeyChanged'; value: string }
  | { type: 'locationChanged'; value: string }
  | { type: 'promptChanged'; value: string }
  | { type: 'yearsChanged'; value: number[] }
  | { type: 'qualityChanged'; value: GenQuality }
  | { type: 'submitStarted' }
  | { type: 'submitCancelled' }
  | { type: 'frameLoaded'; frame: Keyframe }
  | { type: 'frameError'; failure: { year: number; error: Error } }
  | {
      type: 'journeyFinished';
      frames: Keyframe[];
      failures: Array<{ year: number; error: Error }>;
      history: StoredJourney[];
    }
  | { type: 'journeyCrashed'; failure: Error }
  | { type: 'inlineError'; message: string }
  | { type: 'presetPicked'; location: string; years: number[] }
  | { type: 'historyPicked'; item: StoredJourney }
  | { type: 'historyCleared' }
  | { type: 'tChanged'; value: number };

function sortYears(years: readonly number[]): number[] {
  return Array.from(new Set(years)).sort((a, b) => a - b);
}

function sortFrames(frames: readonly Keyframe[]): Keyframe[] {
  return [...frames].sort((a, b) => a.year - b.year);
}

function upsertFrame(frames: readonly Keyframe[], incoming: Keyframe): Keyframe[] {
  const next = frames.filter((frame) => frame.year !== incoming.year);
  next.push(incoming);
  return sortFrames(next);
}

function upsertFailure(
  failures: readonly { year: number; error: Error }[],
  incoming: { year: number; error: Error },
): Array<{ year: number; error: Error }> {
  const next = failures.filter((failure) => failure.year !== incoming.year);
  next.push(incoming);
  return next.sort((a, b) => a.year - b.year);
}

function getRateLimitError(
  failures: readonly { year: number; error: Error }[],
): OpenAIError | undefined {
  return failures.find(
    (failure): failure is { year: number; error: OpenAIError } =>
      failure.error instanceof OpenAIError && failure.error.status === 429,
  )?.error;
}

function getRateLimitToastMessage(error: OpenAIError): string {
  return pt.rateLimited(error.retryAfterSeconds ?? null);
}

function createInitialState(): State {
  return {
    apiKey: getApiKey(),
    location: '',
    prompt: '',
    years: [],
    quality: 'high',
    status: 'idle',
    frames: [],
    failures: [],
    t: 0,
    history: getHistory(),
    inlineError: '',
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'apiKeyChanged':
      return { ...state, apiKey: action.value };
    case 'locationChanged':
      return { ...state, location: action.value, inlineError: '' };
    case 'promptChanged':
      return { ...state, prompt: action.value };
    case 'yearsChanged':
      return { ...state, years: sortYears(action.value), inlineError: '' };
    case 'qualityChanged':
      return { ...state, quality: action.value };
    case 'submitStarted':
      return {
        ...state,
        status: 'loading',
        frames: [],
        failures: [],
        t: 0,
        inlineError: '',
      };
    case 'submitCancelled':
      return {
        ...state,
        status: 'idle',
        frames: [],
        failures: [],
        t: 0,
      };
    case 'frameLoaded':
      return {
        ...state,
        frames: upsertFrame(state.frames, action.frame),
      };
    case 'frameError':
      return {
        ...state,
        failures: upsertFailure(state.failures, action.failure),
      };
    case 'journeyFinished': {
      const nextStatus: Status =
        action.failures.length === 0
          ? 'ready'
          : action.failures.length === state.years.length
            ? 'error'
            : 'partial';

      return {
        ...state,
        status: nextStatus,
        frames: sortFrames(action.frames),
        failures: action.failures,
        history: action.history,
      };
    }
    case 'journeyCrashed':
      return {
        ...state,
        status: 'error',
        failures: upsertFailure(state.failures, { year: Number.NaN, error: action.failure }),
      };
    case 'inlineError':
      return { ...state, inlineError: action.message };
    case 'presetPicked':
      return {
        ...state,
        location: action.location,
        years: sortYears(action.years),
        inlineError: '',
        t: 0,
      };
    case 'historyPicked':
      return {
        ...state,
        location: action.item.location,
        prompt: action.item.prompt,
        years: sortYears(action.item.years),
        frames: sortFrames(
          action.item.frames.map((frame) => ({
            year: frame.year,
            b64: frame.b64,
          })),
        ),
        failures: [],
        status: action.item.frames.length > 0 ? 'ready' : 'idle',
        t: 0,
        inlineError: '',
      };
    case 'historyCleared':
      return { ...state, history: [] };
    case 'tChanged':
      return { ...state, t: action.value };
    default:
      return state;
  }
}

function HomeContent() {
  const [state, dispatch] = useReducer(reducer, undefined, createInitialState);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const runIdRef = useRef(0);
  const inflightRef = useRef<AbortController | null>(null);

  const cancelInflight = () => {
    const controller = inflightRef.current;
    if (!controller) {
      return;
    }

    inflightRef.current = null;
    controller.abort();
    dispatch({ type: 'submitCancelled' });
  };

  useEffect(() => {
    return () => {
      inflightRef.current?.abort();
    };
  }, []);

  const panoramaFrames = useMemo(
    () =>
      state.frames.map((frame) => ({
        id: String(frame.year),
        src: `data:image/png;base64,${frame.b64}`,
        year: frame.year,
      })),
    [state.frames],
  );

  const timelineYears = useMemo(
    () => (state.frames.length > 0 ? state.frames.map((frame) => frame.year) : state.years),
    [state.frames, state.years],
  );

  const shareableFrames = useMemo(
    () => state.frames.map((frame) => ({ year: frame.year, b64: frame.b64 })),
    [state.frames],
  );

  const handleSubmit = async () => {
    const trimmedApiKey = state.apiKey.trim();
    const trimmedLocation = state.location.trim();
    const trimmedPrompt = state.prompt.trim();
    const submittedYears = [...state.years];
    const submittedQuality = state.quality;

    if (!trimmedApiKey) {
      showToast(pt.apiKeyMissingError, 'error');
      return;
    }

    if (!trimmedLocation || submittedYears.length < 2) {
      dispatch({ type: 'inlineError', message: pt.journeyInputError });
      return;
    }

    inflightRef.current?.abort();
    const controller = new AbortController();
    inflightRef.current = controller;
    const myRun = ++runIdRef.current;

    dispatch({ type: 'submitStarted' });

    try {
      const result = await generateJourney({
        location: trimmedLocation,
        years: submittedYears,
        apiKey: trimmedApiKey,
        quality: submittedQuality,
        extraDirections: trimmedPrompt || undefined,
        signal: controller.signal,
        onFrameLoaded: (frame) => {
          if (runIdRef.current !== myRun || controller.signal.aborted) {
            return;
          }

          dispatch({ type: 'frameLoaded', frame });
        },
        onFrameError: (error, year) => {
          if (runIdRef.current !== myRun || controller.signal.aborted) {
            return;
          }

          dispatch({ type: 'frameError', failure: { year, error } });
        },
      });

      if (runIdRef.current !== myRun || controller.signal.aborted) {
        return;
      }

      const journey: StoredJourney = {
        id: crypto.randomUUID(),
        location: trimmedLocation,
        prompt: trimmedPrompt,
        years: submittedYears,
        frames: result.keyframes.map((frame) => ({ year: frame.year, b64: frame.b64 })),
        createdAt: Date.now(),
      };
      const nextHistory = pushHistory(journey);

      dispatch({
        type: 'journeyFinished',
        frames: result.keyframes,
        failures: result.failures,
        history: nextHistory,
      });

      const rateLimitError = getRateLimitError(result.failures);
      if (rateLimitError) {
        showToast(getRateLimitToastMessage(rateLimitError), 'error');
        return;
      }

      if (result.failures.length === submittedYears.length) {
        showToast(pt.journeyNoneGeneratedError, 'error');
      }
    } catch (error: unknown) {
      if (controller.signal.aborted || runIdRef.current !== myRun) {
        return;
      }

      const normalized = error instanceof Error ? error : new Error(String(error));
      dispatch({ type: 'journeyCrashed', failure: normalized });

      if (normalized instanceof OpenAIError) {
        if (normalized.status === 429) {
          showToast(getRateLimitToastMessage(normalized), 'error');
          return;
        }

        showToast(pt.openAiStatusError(normalized.status), 'error');
        return;
      }

      showToast(pt.journeyGenerationError, 'error');
    } finally {
      if (inflightRef.current === controller && runIdRef.current === myRun) {
        inflightRef.current = null;
      }
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-black text-white">
      <div className="absolute inset-x-4 top-4 z-20 sm:inset-x-auto sm:right-4">
        <ApiKeyBar
          value={state.apiKey}
          onChange={(value) => {
            cancelInflight();
            dispatch({ type: 'apiKeyChanged', value });
          }}
          onSave={() => {
            cancelInflight();
            const trimmed = state.apiKey.trim();
            dispatch({ type: 'apiKeyChanged', value: trimmed });
            setApiKey(trimmed);
          }}
          onClear={() => {
            cancelInflight();
            clearApiKey();
            dispatch({ type: 'apiKeyChanged', value: '' });
          }}
        />
      </div>

      <main className="grid min-h-screen grid-cols-12 gap-4 p-4 pt-28 sm:pt-32 lg:pt-4">
        <section className="col-span-12 min-w-0 space-y-4 overflow-y-auto lg:col-span-4 lg:pr-2">
          <div className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-xl">
            <h1 className="text-xs uppercase tracking-[0.28em] text-white/45">{pt.appTitle}</h1>
            <p className="mt-3 text-sm leading-6 text-white/70">{pt.appTagline}</p>
          </div>

          <PromptBar
            disabled={false}
            loading={false}
            location={state.location}
            prompt={state.prompt}
            onLocationChange={(value) => {
              cancelInflight();
              dispatch({ type: 'locationChanged', value });
            }}
            onPromptChange={(value) => {
              cancelInflight();
              dispatch({ type: 'promptChanged', value });
            }}
            onSubmit={() => {
              void handleSubmit();
            }}
          />

          {state.status === 'loading' ? (
            <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm text-white/80">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-white" />
              <span>{pt.generateCtaLoading}</span>
            </div>
          ) : null}

          {state.inlineError ? (
            <p className="rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              {state.inlineError}
            </p>
          ) : null}

          <YearChips
            years={state.years}
            onChange={(value) => {
              cancelInflight();
              dispatch({ type: 'yearsChanged', value });
            }}
          />

          <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <label className="text-sm text-white/80" htmlFor="quality-select">
              {pt.qualityLabel}
            </label>
            <select
              className="rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none"
              id="quality-select"
              value={state.quality}
              onChange={(event) => {
                cancelInflight();
                dispatch({ type: 'qualityChanged', value: event.target.value as GenQuality });
              }}
            >
              <option value="high">{pt.qualityHigh}</option>
              <option value="medium">{pt.qualityMedium}</option>
            </select>
          </div>

          <CostDisclosure quality={state.quality} years={state.years} />

          <Presets
            onPick={(preset) => {
              cancelInflight();
              dispatch({
                type: 'presetPicked',
                location: preset.location,
                years: preset.years,
              });
            }}
          />

          <HistoryRail
            items={state.history}
            onClear={() => {
              clearHistory();
              dispatch({ type: 'historyCleared' });
            }}
            onPick={(item) => {
              cancelInflight();
              dispatch({ type: 'historyPicked', item });
            }}
          />

          <p className="text-xs text-white/60">{pt.keyStoredNote}</p>
          <p className="text-xs text-white/60">
            {pt.footerCredits} · {GPT_IMAGE_2_MODEL}
          </p>
        </section>

        <section className="relative col-span-12 min-h-[45vh] min-w-0 lg:col-span-8 lg:min-h-0">
          <ErrorBoundary>
            <PanoramaViewer
              className="h-full w-full overflow-hidden rounded-2xl"
              frames={panoramaFrames}
              blendT={state.t}
              hint={pt.viewerHint}
              onError={() => showToast(pt.viewerLoadError, 'error')}
            />
          </ErrorBoundary>

          <div className="absolute inset-x-4 bottom-4 space-y-3">
            <TimeSlider
              t={state.t}
              years={timelineYears}
              onChange={(value) => dispatch({ type: 'tChanged', value })}
            />

            <ShareButton
              frames={shareableFrames}
              location={state.location}
              prompt={state.prompt}
              onError={(message) => showToast(message, 'error')}
              onShared={(id) => {
                showToast(pt.shareSuccess);
                navigate(`/j/${id}`);
              }}
            />

            {state.failures.length > 0 ? (
              <button
                className="inline-flex w-full items-center justify-center rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-100 transition hover:bg-amber-500/15"
                type="button"
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {pt.retryFailed}
              </button>
            ) : null}
          </div>

          {state.failures.length > 0 ? (
            <small className="absolute left-4 top-4 rounded-full border border-amber-400/20 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-100">
              {pt.partialLoadMsg(state.frames.length, state.years.length)}
            </small>
          ) : null}
        </section>
      </main>
    </div>
  );
}

export default function Home() {
  return (
    <ToastProvider>
      <HomeContent />
    </ToastProvider>
  );
}
